/* Latest-news slider on the front page.
 *
 * Progressive enhancement: the markup is a horizontally scrollable track with
 * scroll-snap, so it already works by swiping or scrolling. This adds arrows,
 * dots and a 5s rotation on top, and keeps everything in sync when the track
 * is scrolled directly.
 *
 * The rotation pauses while the slider is hovered or focused, while the tab is
 * hidden, and it never runs for readers who ask for reduced motion.
 */
const SELECTOR = '.wb-slider';
const ROTATE_MS = 5000;

const reducedMotion = () =>
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function initSlider(root) {
  const viewport = root.querySelector('.wb-slider-viewport');
  const slides = Array.from(root.querySelectorAll('.wb-slide'));
  const prev = root.querySelector('[data-slider-prev]');
  const next = root.querySelector('[data-slider-next]');
  const dots = root.querySelector('.wb-slider-dots');

  if (!viewport || !dots || slides.length < 2) {
    return;
  }

  let index = 0;
  let timer = null;

  const buttons = slides.map((_, i) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'wb-slider-dot';
    button.setAttribute('aria-label', `Headline ${i + 1} of ${slides.length}`);
    button.addEventListener('click', () => {
      show(i);
      restart();
    });
    dots.appendChild(button);
    return button;
  });

  function paint() {
    buttons.forEach((button, i) => button.classList.toggle('is-active', i === index));
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === slides.length - 1;
  }

  function show(i) {
    index = Math.max(0, Math.min(slides.length - 1, i));
    // Measure from the viewport edges rather than offsetLeft, so it does not
    // depend on which ancestor happens to be the offsetParent.
    const left = slides[index].getBoundingClientRect().left - viewport.getBoundingClientRect().left;
    viewport.scrollLeft += left;
    paint();
  }

  function advance() {
    show((index + 1) % slides.length);
  }

  function start() {
    if (timer || reducedMotion() || document.hidden) return;
    timer = setInterval(advance, ROTATE_MS);
  }

  function stop() {
    if (!timer) return;
    clearInterval(timer);
    timer = null;
  }

  function restart() {
    stop();
    start();
  }

  if (prev) {
    prev.addEventListener('click', () => {
      show(index - 1);
      restart();
    });
  }

  if (next) {
    next.addEventListener('click', () => {
      show(index + 1);
      restart();
    });
  }

  // Reading pauses the rotation; leaving resumes it.
  root.addEventListener('mouseenter', stop);
  root.addEventListener('mouseleave', start);
  root.addEventListener('focusin', stop);
  root.addEventListener('focusout', (event) => {
    if (!root.contains(event.relatedTarget)) start();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else start();
  });

  // Scrolling with a finger or trackpad keeps the arrows and dots honest.
  let queued = false;
  viewport.addEventListener('scroll', () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      const step = viewport.clientWidth + parseFloat(getComputedStyle(viewport).columnGap || '0');
      const visible = Math.round(viewport.scrollLeft / step);
      if (visible !== index) {
        index = Math.max(0, Math.min(slides.length - 1, visible));
        paint();
      }
      queued = false;
    });
  });

  show(0);
  start();
}

function boot() {
  document.querySelectorAll(SELECTOR).forEach(initSlider);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
